import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Calendar, MapPin, Users, IndianRupee, Share2, Copy, Check, Wallet } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { openWhatsAppShare } from "../lib/whatsapp";

const UPI_REGEX = /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z][a-zA-Z0-9.\-_]{2,64}$/;

const todayStr = () => new Date().toISOString().split("T")[0];

export default function CreateTripPage() {
  const navigate = useNavigate();
  const { profile, refreshProfile } = useAuth();

  const [loading, setLoading] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [createdTrip, setCreatedTrip] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const [hostName, setHostName] = useState(profile?.name || "");
  const [form, setForm] = useState({
    trip_name: "",
    destination: "",
    start_date: "",
    end_date: "",
    cost_per_person: "",
    advance_amount: "",
    max_members: "",
    minimum_travelers: "",
    payment_deadline_hours: "48",
    cancellation_policy: "non_refundable",
    host_upi_id: "",
    trip_details: "",
  });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const validate = () => {
    if (!hostName.trim()) {
      toast.error("Please enter your name");
      return false;
    }
    if (!UPI_REGEX.test(form.host_upi_id.trim())) {
      toast.error("Enter a valid UPI ID (e.g. yourname@upi)");
      return false;
    }
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const start = new Date(form.start_date);
    const end = new Date(form.end_date);
    if (start < today) { toast.error("Start date cannot be in the past"); return false; }
    if (end < start) { toast.error("End date cannot be before start date"); return false; }

    const cost = parseFloat(form.cost_per_person);
    const advance = parseFloat(form.advance_amount);
    const maxM = parseInt(form.max_members, 10);
    const minM = parseInt(form.minimum_travelers, 10);

    if (cost <= 0) { toast.error("Cost per person must be greater than 0"); return false; }
    if (advance <= 0) { toast.error("Advance amount must be greater than 0"); return false; }
    if (advance > cost) { toast.error("Advance amount cannot exceed cost per person"); return false; }
    if (maxM < 2) { toast.error("Max members must be at least 2"); return false; }
    if (minM < 2) { toast.error("Minimum travelers must be at least 2"); return false; }
    if (minM > maxM) { toast.error("Minimum travelers cannot exceed max members"); return false; }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);

    try {
      // Update host's name in backend if changed/first time
      if (!profile?.name || profile.name !== hostName.trim()) {
        await api.patch("/users/me", { name: hostName.trim() });
        await refreshProfile();
      }

      const payload = {
        trip_name: form.trip_name.trim(),
        destination: form.destination.trim(),
        start_date: form.start_date,
        end_date: form.end_date,
        cost_per_person: parseFloat(form.cost_per_person),
        advance_amount: parseFloat(form.advance_amount),
        max_members: parseInt(form.max_members, 10),
        minimum_travelers: parseInt(form.minimum_travelers, 10),
        payment_deadline_hours: parseInt(form.payment_deadline_hours, 10),
        cancellation_policy: form.cancellation_policy,
        host_upi_id: form.host_upi_id.trim(),
        trip_details: form.trip_details || "",
      };

      const res = await api.post("/trips", payload);
      setCreatedTrip(res.data);
      setShowShareModal(true);
      toast.success("Trip locked. Time to invite your crew 🚀");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create trip");
    } finally {
      setLoading(false);
    }
  };

  const tripLink = createdTrip ? `${window.location.origin}/trip/${createdTrip.id}` : "";

  const handleCopyLink = () => {
    navigator.clipboard.writeText(tripLink);
    setLinkCopied(true);
    toast.success("Link copied!");
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleWhatsAppShare = () => {
    if (!createdTrip) return;
    openWhatsAppShare(createdTrip, tripLink);
  };

  const inputCls =
    "w-full h-12 bg-white border border-slate-200 hover:border-slate-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 rounded-xl transition-all px-4 outline-none";

  const sectionCls = "bg-white border border-slate-100 rounded-2xl p-5 shadow-sm";
  const sectionTitle = "text-base font-semibold text-slate-900 mb-1";
  const sectionSub = "text-xs text-slate-500 mb-4";

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <div className="max-w-md mx-auto px-4 py-6">
        <div className="flex items-center mb-6">
          <button
            data-testid="back-btn"
            onClick={() => navigate("/")}
            className="mr-4 w-10 h-10 bg-white rounded-full flex items-center justify-center border border-slate-200 active:scale-95 transition-all"
          >
            <ArrowLeft className="w-5 h-5 text-slate-700" />
          </button>
          <div>
            <h1 className="text-3xl font-semibold text-slate-900">Plan a trip</h1>
            <p className="text-sm text-slate-500 mt-1">Give your crew something to look forward to ✈️</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* CARD 1 — WHERE & WHEN */}
          <div className={sectionCls}>
            <h2 className={sectionTitle}>Where &amp; when</h2>
            <p className={sectionSub}>Tell people where they're heading.</p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Trip name</label>
                <input
                  data-testid="trip-name-input"
                  type="text"
                  name="trip_name"
                  value={form.trip_name}
                  onChange={onChange}
                  placeholder="Goa Reset 2026"
                  required
                  className={inputCls}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <MapPin className="w-4 h-4 inline mr-1" /> Destination
                </label>
                <input
                  data-testid="destination-input"
                  type="text"
                  name="destination"
                  value={form.destination}
                  onChange={onChange}
                  placeholder="Goa, Manali, Pondicherry…"
                  required
                  className={inputCls}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <Calendar className="w-4 h-4 inline mr-1" /> Start
                  </label>
                  <input
                    data-testid="start-date-input"
                    type="date"
                    name="start_date"
                    value={form.start_date}
                    onChange={onChange}
                    min={todayStr()}
                    required
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">End</label>
                  <input
                    data-testid="end-date-input"
                    type="date"
                    name="end_date"
                    value={form.end_date}
                    onChange={onChange}
                    min={form.start_date || todayStr()}
                    required
                    className={inputCls}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* CARD 2 — THE CREW */}
          <div className={sectionCls}>
            <h2 className={sectionTitle}>The crew</h2>
            <p className={sectionSub}>How many people you want, and the smallest group that makes the trip worth it.</p>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <Users className="w-4 h-4 inline mr-1" /> Max travelers
                </label>
                <input
                  data-testid="max-members-input"
                  type="number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  name="max_members"
                  value={form.max_members}
                  onChange={onChange}
                  placeholder="10"
                  required
                  min="2"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Min travelers</label>
                <input
                  data-testid="min-travelers-input"
                  type="number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  name="minimum_travelers"
                  value={form.minimum_travelers}
                  onChange={onChange}
                  placeholder="4"
                  required
                  min="2"
                  className={inputCls}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Pay within</label>
                <select
                  data-testid="deadline-select"
                  name="payment_deadline_hours"
                  value={form.payment_deadline_hours}
                  onChange={onChange}
                  className={inputCls}
                >
                  <option value="24">24 hours</option>
                  <option value="48">48 hours</option>
                  <option value="72">72 hours</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Cancellation</label>
                <select
                  data-testid="cancellation-select"
                  name="cancellation_policy"
                  value={form.cancellation_policy}
                  onChange={onChange}
                  className={inputCls}
                >
                  <option value="non_refundable">Non-refundable</option>
                  <option value="50_percent">50% refundable</option>
                  <option value="fully_refundable">Fully refundable</option>
                </select>
              </div>
            </div>
          </div>

          {/* CARD 3 — MONEY */}
          <div className={sectionCls}>
            <h2 className={sectionTitle}>Money</h2>
            <p className={sectionSub}>Type any amount that feels right — no rounding rules.</p>

            <div className="grid grid-cols-2 gap-3 mb-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <IndianRupee className="w-4 h-4 inline mr-1" /> Cost / person
                </label>
                <input
                  data-testid="cost-input"
                  type="number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  name="cost_per_person"
                  value={form.cost_per_person}
                  onChange={onChange}
                  placeholder="8000"
                  required
                  min="1"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Seat lock amount</label>
                <input
                  data-testid="advance-input"
                  type="number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  name="advance_amount"
                  value={form.advance_amount}
                  onChange={onChange}
                  placeholder="1500"
                  required
                  min="1"
                  className={inputCls}
                />
              </div>
            </div>

            {/* UPI block */}
            <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <Wallet className="w-5 h-5 text-emerald-700" />
                <h3 className="text-sm font-semibold text-emerald-900">Your UPI ID</h3>
              </div>
              <p className="text-xs text-emerald-800 mb-3 leading-relaxed">
                Friends pay you directly. TripLatch never holds money.
              </p>
              <input
                data-testid="upi-id-input"
                type="text"
                name="host_upi_id"
                value={form.host_upi_id}
                onChange={onChange}
                placeholder="yourname@upi"
                required
                autoCapitalize="none"
                autoCorrect="off"
                className="w-full h-12 bg-white border border-emerald-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 rounded-xl px-4 outline-none"
              />
            </div>
          </div>

          {/* CARD 4 — HOST NAME */}
          <div className={sectionCls}>
            <h2 className={sectionTitle}>Your name</h2>
            <p className={sectionSub}>Friends will see this when they open the trip.</p>
            <input
              data-testid="host-name-input"
              type="text"
              value={hostName}
              onChange={(e) => setHostName(e.target.value)}
              placeholder="e.g. Rahul"
              required
              className={inputCls}
            />
          </div>

          <button
            data-testid="submit-trip-btn"
            type="submit"
            disabled={loading}
            className="w-full bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed rounded-full h-14 px-8 font-semibold shadow-lg shadow-indigo-600/20 active:scale-95 transition-all"
          >
            {loading ? "Locking..." : "Lock the trip 🔒"}
          </button>

          <p className="text-center text-xs text-slate-500 px-4">
            TripLatch is a coordination platform — payments go directly to you via UPI.
          </p>
        </form>
      </div>

      {showShareModal && createdTrip && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 animate-in">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Trip locked. 🎉</h2>
              <p className="text-sm text-slate-600">
                Now invite your crew. Only people who pay are in.
              </p>
            </div>

            <div className="space-y-3 mb-4">
              <button
                data-testid="copy-link-btn"
                onClick={handleCopyLink}
                className="w-full bg-slate-100 hover:bg-slate-200 text-slate-900 rounded-full h-12 px-6 font-semibold flex items-center justify-center gap-2 active:scale-95 transition-all"
              >
                {linkCopied ? (<><Check className="w-5 h-5 text-emerald-600" /> Link Copied!</>) : (<><Copy className="w-5 h-5" /> Copy Link</>)}
              </button>

              <button
                data-testid="whatsapp-share-btn"
                onClick={handleWhatsAppShare}
                className="w-full bg-[#25D366] text-white hover:bg-[#20bd5a] rounded-full h-12 px-6 font-semibold flex items-center justify-center gap-2 shadow-lg shadow-green-500/20 active:scale-95 transition-all"
              >
                <Share2 className="w-5 h-5" /> Share on WhatsApp
              </button>
            </div>

            <button
              data-testid="view-trip-btn"
              onClick={() => navigate(`/trip/${createdTrip.id}`)}
              className="w-full text-indigo-600 font-medium text-sm hover:text-indigo-700"
            >
              View Trip →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
